import React from 'react';
import { CheckCircle, CreditCard, Scissors, Ruler, AlertCircle, Wallpaper, PackageOpen, Droplet } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета обоев
 */
const ResultsOverview = ({ results, formData }) => {
  if (!results) return null;

  // Получение названия типа обоев
  const getWallpaperTypeName = (typeId) => {
    const wallpaperTypes = {
      'standard': 'Стандартные бумажные',
      'wide': 'Флизелиновые широкие',
      'vinyl': 'Виниловые',
      'nonwoven': 'Флизелиновые',
      'textile': 'Текстильные',
      'photoWall': 'Фотообои',
      'liquid': 'Жидкие обои'
    };
    return wallpaperTypes[typeId] || 'Обои';
  };

  // Получение названия типа клея
  const getGlueTypeName = (typeId) => {
    const glueTypes = {
      'standard': 'Стандартный клей',
      'special': 'Усиленный клей',
      'none': 'Без клея'
    };
    return glueTypes[typeId] || 'Клей';
  };

  return (
    <ToolResult title="Результаты расчета обоев">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Количество рулонов */}
        <div className="rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 p-5 border border-indigo-100 dark:border-indigo-800/50 shadow-sm">
          <h3 className="text-base font-medium text-indigo-700 dark:text-indigo-300 mb-1.5 flex items-center gap-1.5">
            <Wallpaper className="w-4 h-4"/>
            Необходимое количество
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-indigo-800 dark:text-indigo-200" aria-live="polite">
              {results.rollsRequired} {formatRollSuffix(results.rollsRequired, formData.wallpaperType)}
            </span>
          </div>
          <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
            Включает запас материала {formData.wastagePercent}%
          </p>
        </div>

        {/* Общая стоимость */}
        <div className="rounded-lg bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/30 dark:to-teal-900/30 p-5 border border-emerald-100 dark:border-emerald-800/50 shadow-sm">
          <h3 className="text-base font-medium text-emerald-700 dark:text-emerald-300 mb-1.5 flex items-center gap-1.5">
            <CreditCard className="w-4 h-4"/>
            Примерная стоимость
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-emerald-800 dark:text-emerald-200" aria-live="polite">
              {formatNumber(results.totalCost, 0)} ₽
            </span>
          </div>
          <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
            {results.rollsRequired > 0 ? `Обои: ${formatNumber(results.rollsCost, 0)} ₽` : ''} 
            {results.gluePacksRequired > 0 ? `, Клей: ${formatNumber(results.glueCost, 0)} ₽` : ''}
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Тип обоев" 
          value={getWallpaperTypeName(formData.wallpaperType)} 
          color="indigo" 
          icon={<Wallpaper className="w-4 h-4"/>}
        />
        
        {formData.wallpaperType !== 'liquid' && (
          <>
            <ResultItem 
              label="Параметры рулона" 
              value={`${formData.rollWidth} см × ${formData.rollLength} м${formData.patternRepeat > 0 ? `, раппорт ${formData.patternRepeat} см` : ''}`} 
              color="blue" 
              icon={<Ruler className="w-4 h-4"/>}
            />
          </>
        )}
        
        <ResultItem 
          label="Площадь поверхности" 
          value={`${formatNumber(results.netArea, 2)} м² (стены: ${formatNumber(results.wallArea, 2)} м²${results.ceilingArea > 0 ? `, потолок: ${formatNumber(results.ceilingArea, 2)} м²` : ''})`} 
          color="green" 
          icon={<Scissors className="w-4 h-4"/>} 
        />
        
        {results.windowArea > 0 && (
          <ResultItem 
            label="Вычтено проёмов" 
            value={`${formatNumber(results.windowArea + results.doorArea, 2)} м² (окна: ${formatNumber(results.windowArea, 2)} м², двери: ${formatNumber(results.doorArea, 2)} м²)`} 
            color="gray"
          />
        )}

        <ResultItem 
          label="Необходимо рулонов" 
          value={`${results.rollsRequired} ${formatRollSuffix(results.rollsRequired, formData.wallpaperType)}`} 
          color="indigo" 
          icon={<Wallpaper className="w-4 h-4"/>} 
        />
        
        {formData.glueType !== 'none' && (
          <ResultItem 
            label="Необходимо клея" 
            value={`${results.gluePacksRequired} ${formatGlueSuffix(results.gluePacksRequired)} (${getGlueTypeName(formData.glueType)})`} 
            color="orange" 
            icon={<Droplet className="w-4 h-4"/>} 
          />
        )}
        
        {formData.includePrimeCoat && (
          <ResultItem 
            label="Необходимо грунтовки" 
            value={`${results.primeCoatPacksRequired} ${formatPrimeCoatSuffix(results.primeCoatPacksRequired)}`} 
            color="orange" 
            icon={<PackageOpen className="w-4 h-4"/>} 
          />
        )}
        
        <ResultItem 
          label="Стоимость материалов" 
          value={`${formatNumber(results.totalCost, 0)} ₽`} 
          color="green" 
          icon={<CreditCard className="w-4 h-4"/>} 
        />

        {/* Предупреждение о запасе материала */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <AlertCircle className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Рекомендуемый запас материала</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                В расчет включен запас {formData.wastagePercent}%. Рекомендуем приобретать с запасом для учета подгонки рисунка, ошибок при нарезке, а также для возможных ремонтов в будущем.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Информация о стыковке обоев */}
      {formData.patternRepeat > 0 && formData.wallpaperType !== 'liquid' && (
        <div className="bg-blue-50 dark:bg-blue-900/10 rounded-lg p-4 mt-4 border border-blue-100 dark:border-blue-800/50">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">Особенности поклейки обоев с рисунком</h4>
              <p className="text-sm text-blue-600 dark:text-blue-400">
                Выбранные вами обои имеют раппорт (повторяющийся рисунок) {formData.patternRepeat} см. 
                При поклейке необходимо совмещать рисунок между соседними полосами, что учтено в расчете количества рулонов.
              </p>
            </div>
          </div>
        </div>
      )}
    </ToolResult>
  );
};

// Вспомогательные функции для форматирования суффиксов
const formatRollSuffix = (count, type) => {
  if (type === 'liquid') {
    return pluralize(count, 'упаковка', 'упаковки', 'упаковок');
  }
  return pluralize(count, 'рулон', 'рулона', 'рулонов');
};

const formatGlueSuffix = (count) => {
  return pluralize(count, 'упаковка', 'упаковки', 'упаковок');
};

const formatPrimeCoatSuffix = (count) => {
  return pluralize(count, 'литр', 'литра', 'литров');
};

// Функция склонения слов в зависимости от числа
const pluralize = (number, one, two, five) => {
  let n = Math.abs(number);
  n %= 100;
  if (n >= 5 && n <= 20) {
    return five;
  }
  n %= 10;
  if (n === 1) {
    return one;
  }
  if (n >= 2 && n <= 4) {
    return two;
  }
  return five;
};

export default ResultsOverview;